import {
  BottomComponentBar,
  Button,
  Icon,
  PopUpView,
} from '@gripp/shared-ui';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Layout } from '../../styles';
import { Colors } from '../../themes';
import { FormContext } from '../forms';
import type { FilterContentProps } from './filterContent';
import { FilterContent } from './filterContent';
import { filterScreenStyles } from './filterScreenStyles';

export type FilterScreenLayoutProps = FilterContentProps & {
  mode: 'page' | 'modal';
  onBack?: () => void;
  onDismiss?: () => void;
  isVisible?: boolean;
  titleKey: string;
  formMethods: ReturnType<typeof useForm>;
  resultsCount: number;
  viewResultsTranslationKey: string;
  onReset: () => void | Promise<void>;
  canReset: boolean | (() => boolean);
  onSubmit: (data: any) => void | Promise<void>;
};

export function FilterScreenLayout({
  mode,
  onBack,
  onDismiss,
  isVisible,
  titleKey,
  formMethods,
  resultsCount,
  viewResultsTranslationKey,
  onReset,
  canReset,
  onSubmit,
  variant,
  sections,
  control,
  setValue,
  selectedDateType,
  workspaceId,
  isGlobalAdmin,
  isOEM,
}: FilterScreenLayoutProps) {
  const { t } = useTranslation();
  const { handleSubmit } = formMethods;
  const resetEnabled = typeof canReset === 'function' ? canReset() : canReset ?? false;
  const renderMode = mode ?? (Platform.OS === 'web' ? 'modal' : 'page');

  const BackButton = () => (
    <Button
      variant="rounded"
      state="active"
      onPress={renderMode === 'modal' ? onDismiss : onBack}
      style={styles.backButton}
      leftIcon={<Icon name="Chevron Left" size={24} color={Colors.white} />}
    />
  );

  const ViewResultsButton = () => (
    <Button
      style={[
        filterScreenStyles.viewResultsButton,
        !resetEnabled && filterScreenStyles.viewResultsButtonWithMargin,
      ] as any}
      onPress={handleSubmit(onSubmit)}
      state="active"
    >
      {t(viewResultsTranslationKey, { count: resultsCount })}
    </Button>
  );

  const footer = (
    <BottomComponentBar
      backButton={<BackButton />}
      additionalHeaderBlock={<ViewResultsButton />}
      onRightPress={resetEnabled ? onReset : undefined}
    />
  );

  const scrollContentStyle = [
    styles.scrollContent,
    renderMode === 'modal' ? filterScreenStyles.scrollContentModal : filterScreenStyles.scrollContentPage,
    renderMode === 'modal' && styles.modalScrollContent,
  ];

  const filterPage = (
    <FormContext.Provider value={formMethods as UseFormReturn}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitleText}>{t(titleKey)}</Text>
        </View>
        <ScrollView style={styles.scrollView} contentContainerStyle={scrollContentStyle}>
          <FilterContent
            variant={variant}
            sections={sections}
            control={control}
            setValue={setValue}
            selectedDateType={selectedDateType}
            workspaceId={workspaceId}
            isGlobalAdmin={isGlobalAdmin}
            isOEM={isOEM}
          />
        </ScrollView>
        {footer}
      </View>
    </FormContext.Provider>
  );

  if (renderMode === 'modal') {
    return (
      <PopUpView
        isVisible={isVisible ?? false}
        onDismiss={onDismiss ?? (() => {})}
        containerStyle={{
          maxWidth: 600,
          maxHeight: 700,
          width: '90%',
          height: '90%',
        }}
      >
        {filterPage}
      </PopUpView>
    );
  }

  return filterPage;
}

const styles = StyleSheet.create({
  backButton: {
    width: 56,
    height: 56,
    minWidth: 56,
    maxHeight: 56,
    borderRadius: 99,
    marginLeft: 16,
  },
  container: {
    ...Layout.container,
    backgroundColor: Colors.white,
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  headerTitleText: {
    fontSize: 24,
    fontWeight: '600',
    color: Colors.black,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 100,
  },
  modalScrollContent: {
    paddingBottom: 100,
  },
});
